import { useEffect, type CSSProperties } from "react";
import { Link } from "wouter";
import {
  ArrowRight,
  Store,
  Radio,
  CalendarCheck,
  Phone,
  Globe,
  Users,
  Clapperboard,
} from "lucide-react";
import laMirageLogo from "@assets/download.png";
import StoreBadges from "@/components/landing/StoreBadges";
import { applyLandingHead, type HeadDoc } from "@/lib/landingHead";
import { APP_FIRST_CLAIM, APP_FIRST_QUALIFIER } from "@shared/appStore";
import { GOOGLE_RATING_LINE, EVENTS_LINE } from "@shared/venueFacts";

/**
 * lamirage.co logged-out landing — app-first (direction B, 2026-10-07).
 * Spec: docs/superpowers/specs/2026-10-07-lamirage-co-landing-app-launch-design.md §2–3.
 *
 * Hero promotes the LA Mirage app (badges gated by APP_STORES_LIVE via StoreBadges), then a
 * four-feature strip, a text-only trust row, three slim doors (vendor / invited / still
 * choosing), the phone strip and the footer. The footer (policy links + GSTIN/address) is
 * reviewed by payment gateways — keep it byte-for-byte.
 * Static page — no queries, no auth logic. Login path untouched (/login).
 * Hard rules (pinned by pages/__tests__/homeLanding.pins.test.ts): no vehicle-hire copy, no
 * vehicle screenshot, only the venue phone (see the test for the exact banned words).
 */

const serif: CSSProperties = { fontFamily: '"Playfair Display", Georgia, serif' };

const goldGrad: CSSProperties = {
  background: "linear-gradient(120deg,#f6dc9a,#efb700 55%,#c98f00)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const goldFill: CSSProperties = {
  background: "linear-gradient(120deg,#f6dc9a,#efb700 55%,#c98f00)",
};

/** Copied from client-lamirage-in/src/data/site.ts SOCIALS.googleReviews (not imported: separate bundle). */
const GOOGLE_REVIEWS_URL = "https://share.google/B4KfW4DPjXE9mUUVv";

const FEATURES = [
  {
    icon: Radio,
    title: "4K live stream",
    line: "Your ceremony streamed live in 4K to family anywhere, with a replay to keep.",
    src: "/app-launch/livestream.webp",
    h: 1169,
  },
  {
    icon: Globe,
    title: "Wedding website",
    line: "Your own wedding website with event details and RSVPs, published from the app.",
    src: "/app-launch/website.webp",
    h: 1169,
  },
  {
    icon: Users,
    title: "Guests & RSVP",
    line: "Invite guests and watch confirmations arrive, plus-ones counted for you.",
    src: "/app-launch/guests.webp",
    h: 822,
  },
  {
    icon: Clapperboard,
    title: "AI wedding films",
    line: "Story Weaver turns your photos into an entrance film for the venue's LED walls.",
    src: "/app-launch/storyweaver.webp",
    h: 1169,
  },
] as const;

/** A CSS phone frame around one app screenshot. */
function PhoneFrame({
  src,
  alt,
  className = "",
  eager = false,
}: {
  src: string;
  alt: string;
  className?: string;
  eager?: boolean;
}) {
  return (
    <div
      className={`relative rounded-[2.6rem] border border-[#efb700]/25 bg-[#111117] p-[7px] shadow-[0_40px_90px_rgba(0,0,0,0.75),inset_0_0_0_1px_rgba(255,255,255,0.05)] ${className}`}
    >
      <div className="relative overflow-hidden rounded-[2.1rem] bg-black">
        <span className="absolute left-1/2 top-2 z-10 h-[18px] w-[72px] -translate-x-1/2 rounded-full bg-black" aria-hidden />
        <img
          src={src}
          alt={alt}
          width={540}
          height={1169}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          className="block h-auto w-full select-none"
        />
      </div>
    </div>
  );
}

export default function Home() {
  // App-promotional head on the lamirage.co ROOT only; restored on unmount (spec §3).
  useEffect(() => applyLandingHead(document as unknown as HeadDoc, window.location.pathname), []);

  return (
    <div className="min-h-dvh bg-[#07070b] text-[#e8e4da] antialiased overflow-x-clip">
      <style>{`
        @keyframes lmFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        @keyframes lmIn{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
        .lm-in{animation:lmIn .7s ease both}
        .lm-float{animation:lmFloat 7s ease-in-out infinite}
        .lm-float-late{animation:lmFloat 7s ease-in-out 1.6s infinite}
        @media (prefers-reduced-motion:reduce){.lm-in,.lm-float,.lm-float-late{animation:none}}
      `}</style>

      {/* aurora */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
        <div className="absolute -left-[14vw] -top-[18vw] h-[52vw] w-[52vw] rounded-full opacity-50 blur-[90px]" style={{ background: "radial-gradient(circle,rgba(239,183,0,.15),transparent 65%)" }} />
        <div className="absolute -right-[12vw] top-[30vh] h-[44vw] w-[44vw] rounded-full opacity-50 blur-[90px]" style={{ background: "radial-gradient(circle,rgba(120,80,220,.09),transparent 65%)" }} />
      </div>

      {/* nav */}
      <nav className="sticky top-0 z-50 flex items-center justify-between border-b border-white/[0.06] bg-[#07070b]/70 px-5 py-3 backdrop-blur-xl md:px-8">
        <div className="flex items-center gap-3">
          <img src={laMirageLogo} alt="LA Mirage" className="h-10 w-10 rounded-lg object-contain" draggable={false} />
          <div className="leading-tight">
            <div className="text-[15px] font-bold tracking-[0.18em] text-white">LA MIRAGE</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#8b867c]">lamirage.co</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link href="/login">
            <a className="rounded-full px-6 py-2.5 text-sm font-bold text-[#100c02] shadow-[0_8px_30px_rgba(239,183,0,0.25)] transition-transform hover:-translate-y-0.5" style={goldFill}>
              Login
            </a>
          </Link>
        </div>
      </nav>

      {/* hero: the app — copy first on phones, two columns from md */}
      <header className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-10 pt-12 md:grid-cols-[1.1fr_1fr] md:gap-8 md:pt-20">
        <div className="lm-in text-center md:text-left">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#efb700]/30 bg-[#efb700]/[0.07] px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#efb700]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#efb700]" aria-hidden />
            New · iPhone &amp; Android
          </span>
          <h1 className="mt-5 text-[2.6rem] font-extrabold leading-[1.05] tracking-tight text-white md:text-[3.6rem]">
            Your whole wedding,{" "}
            <span style={{ ...serif, ...goldGrad }} className="italic">
              in one app.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-md text-[#cfc9bd] md:mx-0 md:text-lg">
            {APP_FIRST_CLAIM} — plan, invite, stream and relive your day at LA Mirage Koratty.
          </p>
          <p className="mt-1.5 text-xs italic text-[#8b867c]">{APP_FIRST_QUALIFIER}</p>
          <div className="mt-7 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-center md:justify-start">
            <StoreBadges />
            <Link href="/login">
              <a className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-bold text-[#100c02] shadow-[0_8px_30px_rgba(239,183,0,0.3)] transition-transform hover:-translate-y-0.5" style={goldFill}>
                Login on web <ArrowRight className="h-4 w-4" />
              </a>
            </Link>
          </div>
        </div>

        <div className="relative mx-auto flex w-full max-w-[400px] justify-center md:max-w-[460px]">
          <div className="absolute inset-x-6 bottom-4 top-10 -z-10 rounded-full bg-[#efb700]/[0.13] blur-3xl" aria-hidden />
          {/* float animation on the wrapper, tilt on the inner div — one transform each */}
          <div className="lm-float-late relative z-0 mt-10 w-[46%]">
            <div className="translate-x-4 -rotate-[5deg] opacity-90">
              <PhoneFrame src="/app-launch/livestream.webp" alt="LA Mirage app: live stream of the wedding" eager />
            </div>
          </div>
          <div className="lm-float relative z-10 w-[52%]">
            <div className="-translate-x-3 rotate-[2deg]">
              <PhoneFrame src="/app-launch/storyweaver.webp" alt="LA Mirage app: Story Weaver AI wedding films" eager />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pb-8">
        {/* feature strip */}
        <section aria-labelledby="lm-features" className="pt-6 md:pt-10">
          <h2 id="lm-features" className="text-center text-2xl font-extrabold text-white md:text-3xl">
            Everything about your day,{" "}
            <span style={{ ...serif, ...goldGrad }} className="italic">in your pocket.</span>
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <article
                key={f.title}
                className="lm-in group flex flex-col overflow-hidden rounded-3xl border border-white/[0.09] bg-white/[0.028] transition-all duration-200 hover:-translate-y-1 hover:border-[#efb700]/45 hover:shadow-[0_20px_60px_rgba(239,183,0,0.10)]"
                style={{ animationDelay: `${0.05 + i * 0.07}s` }}
              >
                <div className="p-6 pb-4">
                  <f.icon className="h-7 w-7 text-[#efb700]" />
                  <h3 className="mt-3 text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#cfc9bd]">{f.line}</p>
                </div>
                <div className="relative mx-6 mt-auto h-48 overflow-hidden rounded-t-[1.6rem] border border-b-0 border-[#efb700]/20 bg-black">
                  <img
                    src={f.src}
                    alt={`LA Mirage app: ${f.title}`}
                    width={540}
                    height={f.h}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="block h-full w-full select-none object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#07070b]/80 to-transparent" aria-hidden />
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* trust row — text only, no rating markup */}
        <section aria-label="LA Mirage in numbers" className="mt-12 flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#efb700]/20 bg-gradient-to-r from-[#efb700]/[0.06] via-white/[0.02] to-[#efb700]/[0.06] px-6 py-5 text-center sm:flex-row sm:gap-10">
          <span className="text-sm font-semibold uppercase tracking-[0.18em] text-[#e8e4da]">{EVENTS_LINE}</span>
          <span className="hidden h-5 w-px bg-white/15 sm:block" aria-hidden />
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-[#efb700] hover:underline"
          >
            {GOOGLE_RATING_LINE}
          </a>
        </section>

        {/* doors */}
        <section aria-label="Other ways in" className="mt-12 grid gap-5 md:grid-cols-3">
          <a href="/vendor-signup" className="group relative block rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#efb700]/50">
            <Store className="h-7 w-7 text-[#efb700]" />
            <h2 className="mt-4 text-xl font-extrabold text-white">
              I&apos;m a wedding <span style={{ ...serif, ...goldGrad }} className="italic">vendor.</span>
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
              Partner with LA Mirage — premium exposure to couples and verified status in our
              approved directory.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#efb700]">
              Join our vendor network <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>

          <a
            href="https://lamirage.live/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#efb700]/50"
          >
            <Radio className="h-7 w-7 text-[#efb700]" />
            <h2 className="mt-4 text-xl font-extrabold text-white">
              I&apos;m invited to a <span style={{ ...serif, ...goldGrad }} className="italic">wedding.</span>
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
              Watch live in 4K on LA Mirage Live, send your wishes and find your photos — from the
              link the couple sent you. No app needed.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#e8e4da] group-hover:text-[#efb700]">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" /></span>
              Watch live — lamirage.live <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>

          <a
            href="https://la-mirage.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#efb700]/50"
          >
            <CalendarCheck className="h-7 w-7 text-[#efb700]" />
            <h2 className="mt-4 text-xl font-extrabold text-white">
              Still choosing a <span style={{ ...serif, ...goldGrad }} className="italic">venue?</span>
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
              See LA Mirage Koratty — the venue, real weddings, pricing and live date availability.
              Book it, and the app comes with it.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#e8e4da] group-hover:text-[#efb700]">
              Visit la-mirage.in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>
        </section>

        {/* slim utility strip */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-6 py-4 text-sm text-[#8b867c]">
          <a href="tel:+919446776666" className="inline-flex items-center gap-2 font-semibold text-[#cfc9bd] hover:text-[#efb700]">
            <Phone className="h-4 w-4 text-[#efb700]" /> +91 94467 76666
          </a>
        </div>
      </main>

      {/* footer */}
      <footer className="mt-6 border-t border-white/[0.07] px-6 py-8 text-center text-xs text-[#8b867c]">
        {/* Company + policy pages are server-rendered (server/publicPages) — plain <a>, full
            page load, so gateway/bank reviewers and crawlers see them without JavaScript. */}
        <p>
          <a href="/about" className="text-[#efb700] hover:underline">About</a>
          {" · "}
          <a href="/contact" className="text-[#efb700] hover:underline">Contact</a>
          {" · "}
          <a href="/privacy-policy" className="text-[#efb700] hover:underline">Privacy Policy</a>
          {" · "}
          <a href="/terms-and-conditions" className="text-[#efb700] hover:underline">Terms &amp; Conditions</a>
          {" · "}
          <a href="/refund-policy" className="text-[#efb700] hover:underline">Refund &amp; Cancellation Policy</a>
          {" · "}
          <a href="https://la-mirage.in/" target="_blank" rel="noopener noreferrer" className="text-[#efb700] hover:underline">Venue website</a>
        </p>
        <p className="mt-3 text-[#8b867c]">
          LA Mirage Convention Center · Manjalaykettu, Old NH, Koratty, Kerala 680308, India · GSTIN 32AAIFL4120G1ZT
        </p>
        <p className="mt-1 text-[#8b867c]">
          <a href="tel:+919446776666" className="hover:underline">+91 9446 77 6666</a>
          {" · "}
          <a href="mailto:sales@lamirage.co" className="hover:underline">sales@lamirage.co</a>
        </p>
        <p className="mt-2 text-[#6d685f]">© 2022–2026 LA Mirage Koratty. The platform, its software and design are proprietary.</p>
      </footer>
    </div>
  );
}
